(() => {
  if (window.__awArticleSliderLoaded) {
    return;
  }

  window.__awArticleSliderLoaded = true;

  const instances = new Map();
  const sliderUtils = window.AWSliderCoreUtils;
  const selectors = {
    root: "[data-aw-article-list]",
    viewport: "[data-aw-article-viewport]",
    list: "[data-aw-article-list-track]",
    item: "[data-aw-article-item]",
    scrollbar: "[data-aw-article-scrollbar]",
    thumb: "[data-aw-article-scrollbar-thumb]",
  };

  class AWArticleSlider {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.viewport = root.querySelector(selectors.viewport);
      this.list = root.querySelector(selectors.list);
      this.items = Array.from(root.querySelectorAll(selectors.item));
      this.scrollbar = root.querySelector(selectors.scrollbar);
      this.thumb = root.querySelector(selectors.thumb);
      this.rafId = null;
      this.teardownResizeObserver = null;
      this.pointerId = null;
      this.startX = 0;
      this.startScrollLeft = 0;
      this.hasDragged = false;
      this.preventClick = false;

      this.onScroll = this.requestUpdate.bind(this);
      this.onResize = this.update.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerEnd.bind(this);
      this.onPointerCancel = this.handlePointerEnd.bind(this);
      this.onClickCapture = this.handleClickCapture.bind(this);
      this.onImageLoad = this.requestUpdate.bind(this);

      this.bindEvents();
      this.update();
    }

    bindEvents() {
      if (this.viewport) {
        this.viewport.addEventListener("scroll", this.onScroll, { passive: true });
        this.viewport.addEventListener("pointerdown", this.onPointerDown);
        this.viewport.addEventListener("pointermove", this.onPointerMove);
        this.viewport.addEventListener("pointerup", this.onPointerUp);
        this.viewport.addEventListener("pointercancel", this.onPointerCancel);
        this.viewport.addEventListener("click", this.onClickCapture, true);
      }

      this.teardownResizeObserver = sliderUtils.observeResize([this.root, this.list], this.onResize);
      sliderUtils.bindImageLoad(this.root, this.onImageLoad);
    }

    getMaxScroll() {
      return sliderUtils.getMaxScroll(this.viewport);
    }

    getScrollBehavior() {
      return sliderUtils.getScrollBehavior();
    }

    requestUpdate() {
      if (this.rafId) {
        return;
      }

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    update() {
      if (!this.viewport) {
        return;
      }

      const maxScroll = this.getMaxScroll();
      const canScroll = maxScroll > 1;
      const scrollLeft = Math.max(0, this.viewport.scrollLeft);

      if (this.scrollbar) {
        this.scrollbar.classList.toggle("is-hidden", !canScroll);
      }

      sliderUtils.updateScrollbar({
        viewport: this.viewport,
        scrollbar: this.scrollbar,
        thumb: this.thumb,
        enabled: canScroll,
      });
    }

    handlePointerDown(event) {
      if (!this.viewport || event.pointerType !== "mouse" || event.button !== 0 || this.getMaxScroll() <= 1) {
        return;
      }

      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startScrollLeft = this.viewport.scrollLeft;
      this.hasDragged = false;

      if (typeof this.viewport.setPointerCapture === "function") {
        this.viewport.setPointerCapture(event.pointerId);
      }
    }

    handlePointerMove(event) {
      if (!this.viewport || this.pointerId !== event.pointerId) {
        return;
      }

      const delta = event.clientX - this.startX;

      if (!this.hasDragged && Math.abs(delta) > 5) {
        this.hasDragged = true;
        this.viewport.classList.add("is-dragging");
      }

      if (!this.hasDragged) {
        return;
      }

      event.preventDefault();
      this.viewport.scrollLeft = this.startScrollLeft - delta;
      this.requestUpdate();
    }

    handlePointerEnd(event) {
      if (!this.viewport || this.pointerId !== event.pointerId) {
        return;
      }

      if (typeof this.viewport.releasePointerCapture === "function" && this.viewport.hasPointerCapture(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }

      if (this.hasDragged) {
        this.preventClick = true;
        window.setTimeout(() => {
          this.preventClick = false;
        }, 300);
      }

      this.pointerId = null;
      this.hasDragged = false;
      this.viewport.classList.remove("is-dragging");
    }

    handleClickCapture(event) {
      if (!this.preventClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.preventClick = false;
    }

    selectBlock(blockId, fallbackTarget) {
      let item = blockId ? this.root.querySelector(`${selectors.item}[data-block-id="${blockId}"]`) : null;

      if (!item && fallbackTarget && typeof fallbackTarget.closest === "function") {
        item = fallbackTarget.closest(selectors.item);
      }

      if (!item || !this.viewport) {
        return;
      }

      sliderUtils.selectBlockItem({
        root: this.root,
        viewport: this.viewport,
        itemSelector: selectors.item,
        blockId,
        fallbackTarget,
      });

      this.requestUpdate();
    }

    destroy() {
      sliderUtils.cancelFrame(this, "rafId");

      if (this.viewport) {
        this.viewport.removeEventListener("scroll", this.onScroll);
        this.viewport.removeEventListener("pointerdown", this.onPointerDown);
        this.viewport.removeEventListener("pointermove", this.onPointerMove);
        this.viewport.removeEventListener("pointerup", this.onPointerUp);
        this.viewport.removeEventListener("pointercancel", this.onPointerCancel);
        this.viewport.removeEventListener("click", this.onClickCapture, true);
      }

      if (this.teardownResizeObserver) {
        this.teardownResizeObserver();
        this.teardownResizeObserver = null;
      }

      sliderUtils.unbindImageLoad(this.root, this.onImageLoad);
    }
  }

  function getRoot(target) {
    if (!target) {
      return null;
    }

    if (typeof target.matches === "function" && target.matches(selectors.root)) {
      return target;
    }

    if (typeof target.querySelector === "function") {
      const root = target.querySelector(selectors.root);

      if (root) {
        return root;
      }
    }

    if (typeof target.closest === "function") {
      return target.closest(selectors.root);
    }

    return null;
  }

  function destroySection(sectionId) {
    const instance = instances.get(sectionId);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initSection(root) {
    if (!root) {
      return;
    }

    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      return;
    }

    destroySection(sectionId);
    instances.set(sectionId, new AWArticleSlider(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      initSection(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSection(getRoot(event.target));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = getRoot(event.target);

    if (!root) {
      return;
    }

    destroySection(root.dataset.sectionId);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const blockId = event.detail && event.detail.blockId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.selectBlock(blockId, event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
